import { useState, useMemo } from 'react';
import { Plus, Loader2, PackageSearch, CheckCircle2, RotateCcw, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import {
  useLostFound,
  useLostFoundItem,
  useCreateLostFound,
  useUpdateLostFound,
} from '@/hooks/api/useLostFound';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { LostFoundStatus, type LostFoundItem } from '@/services/api/frontdeskOps.types';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<LostFoundStatus, { bg: string; text: string; label: string }> = {
  [LostFoundStatus.OPEN]: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Open' },
  [LostFoundStatus.CLAIMED]: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Claimed' },
  [LostFoundStatus.RETURNED]: { bg: 'bg-green-100', text: 'text-green-700', label: 'Returned' },
  [LostFoundStatus.DISPOSED]: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Disposed' },
};

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function LostFoundPage() {
  const propertyId = useSelectedPropertyId();
  const [filterStatus, setFilterStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [showLog, setShowLog] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQuery = useLostFound(
    {
      propertyId,
      status: (filterStatus as LostFoundStatus) || undefined,
      from: fromDate || undefined,
      to: toDate || undefined,
      page,
      limit: PAGE_SIZE,
    },
    !!propertyId,
  );

  const allItems: LostFoundItem[] = listQuery.data?.data ?? [];
  const totalPages = listQuery.data?.meta?.totalPages ?? 1;

  const items = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter(
      (i) => i.description.toLowerCase().includes(q) || i.location.toLowerCase().includes(q),
    );
  }, [allItems, search]);

  return (
    <div>
      <PageHeader
        title="Lost & Found"
        description="Logged lost-and-found items"
        actions={
          <Button onClick={() => setShowLog(true)} disabled={!propertyId}>
            <Plus size={16} aria-hidden="true" />
            Log Item
          </Button>
        }
      />

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="Filter by status"
        >
          <option value="">All Statuses</option>
          {(Object.entries(STATUS_STYLES) as [LostFoundStatus, { label: string }][]).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search item or location..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-60"
          aria-label="Search lost and found"
        />
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="From found date"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="To found date"
          />
        </div>
      </div>

      {/* Table */}
      {!propertyId ? (
        <EmptyState icon={PackageSearch} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={PackageSearch}
          title="No items logged"
          description="Log a lost-and-found item to get started."
          actionLabel="Log Item"
          onAction={() => setShowLog(true)}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Item</Th>
                  <Th>Guest</Th>
                  <Th>Found Date</Th>
                  <Th>Location</Th>
                  <Th>Status</Th>
                  <Th>Linked Booking</Th>
                  <Th>Logged By</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((i) => {
                  const stat = STATUS_STYLES[i.status];
                  return (
                    <tr
                      key={i.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedId(i.id)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium max-w-xs truncate">{i.description}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {i.guestName || i.guestMobile ? (
                          <div>
                            <span className="text-gray-900">{i.guestName ?? '--'}</span>
                            {i.guestMobile && (
                              <span className="block text-xs text-gray-500">{i.guestMobile}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{fmtDate(i.foundDate)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{i.location}</td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', stat.bg, stat.text)}>
                          {stat.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {i.booking?.guestName ?? (i.bookingId ? i.bookingId.slice(0, 8) : '--')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{i.loggedByName ?? '--'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Log slide-over */}
      <SlideOver isOpen={showLog} onClose={() => setShowLog(false)} title="Log Lost & Found Item">
        <LogItemForm propertyId={propertyId} onClose={() => setShowLog(false)} />
      </SlideOver>

      {/* Detail slide-over */}
      <SlideOver
        isOpen={selectedId !== null}
        onClose={() => setSelectedId(null)}
        title="Lost & Found Item"
        width="max-w-xl"
      >
        {selectedId !== null && <LostFoundDetail id={selectedId} />}
      </SlideOver>
    </div>
  );
}

// ─── Log form ────────────────────────────────────────────────────────────────

function LogItemForm({
  propertyId,
  onClose,
}: {
  propertyId: string;
  onClose: () => void;
}) {
  const [description, setDescription] = useState('');
  const [foundDate, setFoundDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [location, setLocation] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestMobile, setGuestMobile] = useState('');
  const [status, setStatus] = useState<LostFoundStatus>(LostFoundStatus.OPEN);
  const [bookingId, setBookingId] = useState('');
  const [notes, setNotes] = useState('');

  const createMutation = useCreateLostFound();
  const isValid =
    !!propertyId && description.trim().length > 0 && !!foundDate && location.trim().length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    createMutation.mutate(
      {
        propertyId,
        description: description.trim(),
        foundDate,
        location: location.trim(),
        status,
        ...(guestName.trim() ? { guestName: guestName.trim() } : {}),
        ...(guestMobile.trim() ? { guestMobile: guestMobile.trim() } : {}),
        ...(bookingId.trim() ? { bookingId: bookingId.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="lf-desc" className="block text-sm font-medium text-gray-700 mb-1">
          Description <span className="text-red-500">*</span>
        </label>
        <input
          id="lf-desc"
          type="text"
          required
          value={description}
          onChange={(e) => { setDescription(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="lf-date" className="block text-sm font-medium text-gray-700 mb-1">
            Found Date <span className="text-red-500">*</span>
          </label>
          <input
            id="lf-date"
            type="date"
            required
            value={foundDate}
            onChange={(e) => { setFoundDate(e.target.value); clearError(); }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="lf-status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            id="lf-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as LostFoundStatus)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {(Object.entries(STATUS_STYLES) as [LostFoundStatus, { label: string }][]).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="lf-location" className="block text-sm font-medium text-gray-700 mb-1">
          Location <span className="text-red-500">*</span>
        </label>
        <input
          id="lf-location"
          type="text"
          required
          value={location}
          onChange={(e) => { setLocation(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="lf-guest-name" className="block text-sm font-medium text-gray-700 mb-1">
            Guest Name
          </label>
          <input
            id="lf-guest-name"
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Optional"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="lf-guest-mobile" className="block text-sm font-medium text-gray-700 mb-1">
            Guest Mobile
          </label>
          <input
            id="lf-guest-mobile"
            type="tel"
            value={guestMobile}
            onChange={(e) => setGuestMobile(e.target.value)}
            placeholder="Optional"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      <div>
        <label htmlFor="lf-booking" className="block text-sm font-medium text-gray-700 mb-1">
          Linked Booking ID
        </label>
        <input
          id="lf-booking"
          type="text"
          value={bookingId}
          onChange={(e) => setBookingId(e.target.value)}
          placeholder="Optional booking UUID"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label htmlFor="lf-notes" className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          id="lf-notes"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      {createMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(createMutation.error)}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending} disabled={!isValid}>Log Item</Button>
      </div>
    </form>
  );
}

// ─── Detail view ─────────────────────────────────────────────────────────────

function LostFoundDetail({ id }: { id: string }) {
  const { data: detail, isLoading } = useLostFoundItem(id);
  const updateMutation = useUpdateLostFound();

  if (isLoading || !detail) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  const stat = STATUS_STYLES[detail.status];

  function transition(status: LostFoundStatus) {
    updateMutation.mutate({ id, payload: { status } });
  }

  const isOpen = detail.status === LostFoundStatus.OPEN;
  const isClaimed = detail.status === LostFoundStatus.CLAIMED;
  const isTerminal = detail.status === LostFoundStatus.RETURNED || detail.status === LostFoundStatus.DISPOSED;

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">{detail.description}</h3>
        <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', stat.bg, stat.text)}>
          {stat.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-500">Found Date</span>
          <p className="font-medium text-gray-900">{fmtDate(detail.foundDate)}</p>
        </div>
        <div>
          <span className="text-gray-500">Location</span>
          <p className="font-medium text-gray-900">{detail.location}</p>
        </div>
        <div>
          <span className="text-gray-500">Guest Name</span>
          <p className="font-medium text-gray-900">{detail.guestName ?? '--'}</p>
        </div>
        <div>
          <span className="text-gray-500">Guest Mobile</span>
          <p className="font-medium text-gray-900">{detail.guestMobile ?? '--'}</p>
        </div>
        <div>
          <span className="text-gray-500">Logged By</span>
          <p className="font-medium text-gray-900">{detail.loggedByName ?? '--'}</p>
        </div>
        <div>
          <span className="text-gray-500">Linked Booking</span>
          <p className="font-medium text-gray-900">
            {detail.bookingId ? (
              <Link to={`/reservations?open=${detail.bookingId}`} className="text-primary-600 hover:underline">
                {detail.booking?.guestName ?? detail.bookingId.slice(0, 8)} — View in Reservations
              </Link>
            ) : (
              '--'
            )}
          </p>
        </div>
      </div>

      {detail.notes && (
        <div>
          <span className="text-sm text-gray-500">Notes</span>
          <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{detail.notes}</p>
        </div>
      )}

      {/* Status transitions */}
      {!isTerminal && (
        <div className="flex items-center gap-3 pt-4 border-t border-gray-100 flex-wrap">
          {isOpen && (
            <Button size="sm" onClick={() => transition(LostFoundStatus.CLAIMED)} isLoading={updateMutation.isPending}>
              <CheckCircle2 size={14} aria-hidden="true" />
              Mark Claimed
            </Button>
          )}
          {(isOpen || isClaimed) && (
            <Button size="sm" onClick={() => transition(LostFoundStatus.RETURNED)} isLoading={updateMutation.isPending}>
              <RotateCcw size={14} aria-hidden="true" />
              Mark Returned
            </Button>
          )}
          {(isOpen || isClaimed) && (
            <Button size="sm" variant="danger" onClick={() => transition(LostFoundStatus.DISPOSED)} isLoading={updateMutation.isPending}>
              <Trash2 size={14} aria-hidden="true" />
              Mark Disposed
            </Button>
          )}
        </div>
      )}

      {updateMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(updateMutation.error)}
        </p>
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
